import React, { useState, useEffect } from 'react';
import { FiEdit, FiSave, FiX, FiCheck, FiAlertCircle, FiTrendingUp, FiTrendingDown, FiActivity, FiBarChart } from 'react-icons/fi';

interface Crop {
  id: number;
  name: string;
  name_hindi: string;
  icon: string;
}

interface MarketCondition {
  id: number;
  crop_id: number;
  crop_name: string;
  crop_name_hindi: string;
  description: string;
  description_english: string | null;
  status: 'stable' | 'rising' | 'falling' | 'volatile';
  created_at: string;
  updated_at: string;
  is_active: number;
}

const API_BASE = 'https://mandilive.in/api';

const MarketConditions: React.FC = () => {
  const [crops, setCrops] = useState<Crop[]>([]);
  const [marketConditions, setMarketConditions] = useState<MarketCondition[]>([]);
  const [selectedCrop, setSelectedCrop] = useState<number | null>(null);
  const [editingCondition, setEditingCondition] = useState<MarketCondition | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form state for adding/editing market conditions
  const [formData, setFormData] = useState({
    description: '',
    description_english: '',
    status: 'stable' as 'stable' | 'rising' | 'falling' | 'volatile',
    is_active: 1
  });

  useEffect(() => {
    fetchCrops();
    fetchMarketConditions();
  }, []);

  const fetchCrops = async () => {
    try {
      console.log('🌱 Fetching crops from:', `${API_BASE}/crops`);
      const response = await fetch(`${API_BASE}/crops`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('🌱 Crops API response:', data);
      
      if (data && data.data && Array.isArray(data.data)) {
        setCrops(data.data);
        console.log('✅ Crops loaded from API:', data.data.length, 'crops');
        console.log('📋 Crop names:', data.data.map(c => `${c.name} (${c.name_hindi})`));
      } else {
        throw new Error('Invalid data format from crops API');
      }
    } catch (err: any) {
      console.error('❌ Failed to load crops from API:', err.message);
      setError(`Failed to load crops: ${err.message}`);
      setCrops([]); // Set empty array instead of hardcoded data
    }
  };

  const fetchMarketConditions = async () => {
    setLoading(true);
    try {
      console.log('Fetching market conditions from:', `${API_BASE}/endpoints/market_conditions.php`);
      const response = await fetch(`${API_BASE}/endpoints/market_conditions.php`);
      
      console.log('Market conditions response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      // Check if response has content
      const text = await response.text();
      console.log('Raw market conditions response:', text);
      
      if (!text.trim()) {
        console.warn('Empty response from market conditions API');
        setMarketConditions([]);
        return;
      }
      
      const data = JSON.parse(text);
      console.log('Parsed market conditions data:', data);
      
      if (data.setup_required) {
        setError('Market conditions table not found. Please set up the database first.');
        setMarketConditions([]);
      } else if (data && data.data && Array.isArray(data.data)) {
        setMarketConditions(data.data);
        console.log('Market conditions loaded successfully:', data.data.length);
      } else {
        console.warn('Market conditions API returned unexpected format:', data);
        setMarketConditions([]);
      }
    } catch (err: any) {
      console.error('Error fetching market conditions:', err);
      console.error('Error stack:', err.stack);
      
      // More user-friendly error message
      if (err.message.includes('Failed to fetch')) {
        setError('Unable to connect to market conditions API. Please check your internet connection and try again.');
      } else if (err.message.includes('JSON')) {
        setError('Invalid response from market conditions API. The server may be experiencing issues.');
      } else {
        setError(`Failed to load market conditions: ${err.message}`);
      }
      
      setMarketConditions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCropSelect = (cropId: number) => {
    setSelectedCrop(cropId);
    const existingCondition = marketConditions.find(mc => mc.crop_id === cropId);
    
    if (existingCondition) {
      setEditingCondition(existingCondition);
      setFormData({
        description: existingCondition.description,
        description_english: existingCondition.description_english || '',
        status: existingCondition.status,
        is_active: existingCondition.is_active
      });
    } else {
      setEditingCondition(null);
      setFormData({
        description: '',
        description_english: '',
        status: 'stable',
        is_active: 1
      });
    }
    setError('');
    setSuccess('');
  };

  const handleSave = async () => {
    if (!selectedCrop) return;
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      const payload = {
        crop_id: selectedCrop,
        description: formData.description,
        description_english: formData.description_english || null,
        status: formData.status,
        is_active: formData.is_active
      };

      const response = await fetch(`${API_BASE}/endpoints/market_conditions.php`, {
        method: editingCondition ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to save market condition');
      }

      const result = await response.json();
      setSuccess(result.message || 'Market condition saved successfully!');
      
      // Refresh the data
      await fetchMarketConditions();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
      
    } catch (err: any) {
      console.error('Error saving market condition:', err);
      setError('Failed to save market condition: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'rising': return <FiTrendingUp className="text-green-600" />;
      case 'falling': return <FiTrendingDown className="text-red-600" />;
      case 'volatile': return <FiActivity className="text-orange-600" />;
      default: return <FiBarChart className="text-blue-600" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'rising': return 'बढ़ रहा (Rising)';
      case 'falling': return 'गिर रहा (Falling)';
      case 'volatile': return 'अस्थिर (Volatile)';
      default: return 'स्थिर (Stable)';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'rising': return 'bg-green-100 text-green-800 border-green-200';
      case 'falling': return 'bg-red-100 text-red-800 border-red-200';
      case 'volatile': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-blue-100 text-blue-800 border-blue-200';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
        <span className="ml-3 text-gray-600">Loading market conditions...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Market Conditions Management</h1>
        <p className="text-gray-600 mt-1">
          Manage market conditions (बाजार की स्थिति) for different crops. Select a crop to view or edit its market condition.
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-md flex items-center">
          <FiCheck className="mr-2" />
          {success}
        </div>
      )}
      
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-md flex items-center">
          <FiAlertCircle className="mr-2" />
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Panel - Crop Selection */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Select Crop</h2>
            <div className="space-y-2 max-h-96 overflow-y-auto">
              {crops.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-gray-400 text-4xl mb-2">🌾</div>
                  <p className="text-gray-500">No crops available.</p>
                  <p className="text-sm text-gray-400">Please check your internet connection or contact support.</p>
                </div>
              ) : (
                crops.map((crop) => {
                const hasCondition = marketConditions.some(mc => mc.crop_id === crop.id);
                const condition = marketConditions.find(mc => mc.crop_id === crop.id);
                
                return (
                  <button
                    key={crop.id}
                    onClick={() => handleCropSelect(crop.id)}
                    className={`w-full text-left p-3 rounded-lg border transition-colors ${
                      selectedCrop === crop.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-900">
                          {crop.name_hindi} ({crop.name})
                        </p>
                        <p className="text-sm text-gray-500">ID: {crop.id}</p>
                      </div>
                      <div className="flex items-center space-x-2">
                        {hasCondition && condition && (
                          <div className="flex items-center">
                            {getStatusIcon(condition.status)}
                            <span className={`ml-2 px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(condition.status)}`}>
                              {getStatusText(condition.status)}
                            </span>
                          </div>
                        )}
                        {!hasCondition && (
                          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-1 rounded-full">
                            No condition
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })
              )}
            </div>
          </div>
        </div>

        {/* Right Panel - Market Condition Form */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6">
            {selectedCrop ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    {editingCondition ? 'Edit' : 'Add'} Market Condition
                  </h2>
                  <div className="text-sm text-gray-500">
                    Crop ID: {selectedCrop}
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Market Condition in Hindi */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Market Condition (Hindi) <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="बाजार की स्थिति का विवरण दें..."
                      required
                    />
                  </div>

                  {/* Market Condition in English */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Market Condition (English) <span className="text-gray-400">(Optional)</span>
                    </label>
                    <textarea
                      value={formData.description_english}
                      onChange={(e) => setFormData({ ...formData, description_english: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      placeholder="Enter market condition description in English..."
                    />
                  </div>

                  {/* Status */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Market Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    >
                      <option value="stable">Stable (स्थिर)</option>
                      <option value="rising">Rising (बढ़ रहा)</option>
                      <option value="falling">Falling (गिर रहा)</option>
                      <option value="volatile">Volatile (अस्थिर)</option>
                    </select>
                  </div>

                  {/* Active Status */}
                  <div>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.is_active === 1}
                        onChange={(e) => setFormData({ ...formData, is_active: e.target.checked ? 1 : 0 })}
                        className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active</span>
                    </label>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4">
                    <button
                      onClick={handleSave}
                      disabled={saving || !formData.description.trim()}
                      className="w-full flex items-center justify-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {saving ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Saving...
                        </>
                      ) : (
                        <>
                          <FiSave className="mr-2" />
                          {editingCondition ? 'Update' : 'Save'} Market Condition
                        </>
                      )}
                    </button>
                  </div>
                </div>

                {/* Current Condition Preview */}
                {editingCondition && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h3 className="text-sm font-medium text-gray-700 mb-2">Current Condition:</h3>
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">
                        <strong>Hindi:</strong> {editingCondition.description}
                      </p>
                      {editingCondition.description_english && (
                        <p className="text-sm text-gray-600">
                          <strong>English:</strong> {editingCondition.description_english}
                        </p>
                      )}
                      <div className="flex items-center">
                        <strong className="text-sm text-gray-600 mr-2">Status:</strong>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(editingCondition.status)}`}>
                          {getStatusText(editingCondition.status)}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500">
                        Last updated: {new Date(editingCondition.updated_at).toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 text-6xl mb-4">📊</div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Crop</h3>
                <p className="text-gray-500">
                  Choose a crop from the list to view or edit its market condition.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Market Conditions Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Market Conditions Summary</h2>
          
          {marketConditions.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 text-4xl mb-2">📈</div>
              <p className="text-gray-500">No market conditions found.</p>
              <p className="text-sm text-gray-400">Add market conditions for crops to see them here.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {marketConditions.map((condition) => (
                <div key={condition.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">
                      {condition.crop_name_hindi} ({condition.crop_name})
                    </h3>
                    {getStatusIcon(condition.status)}
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-2 line-clamp-2">
                    {condition.description}
                  </p>
                  
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(condition.status)}`}>
                      {getStatusText(condition.status)}
                    </span>
                    <button
                      onClick={() => handleCropSelect(condition.crop_id)}
                      className="text-green-600 hover:text-green-700 text-sm flex items-center"
                    >
                      <FiEdit className="w-3 h-3 mr-1" />
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MarketConditions;
